import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@/app/core/auth.interceptor';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { IMAGE_CONFIG } from '@angular/common';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import { catchError, of, firstValueFrom } from 'rxjs';
import { appRoutes } from './app.routes';
import Material from '@primeuix/themes/material';
import { definePreset } from '@primeuix/themes';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { AuthService } from '@/app/auth/auth.service';

const MyPreset = definePreset(Material, {
    semantic: {
        primary: {
            50: '{indigo.50}',
            100: '{indigo.100}',
            200: '{indigo.200}',
            300: '{indigo.300}',
            400: '{indigo.400}',
            500: '{indigo.500}',
            600: '{indigo.600}',
            700: '{indigo.700}',
            800: '{indigo.800}',
            900: '{indigo.900}',
            950: '{indigo.950}'
        }
    }
});

function initializeAuth(auth: AuthService) {
    return () => firstValueFrom(auth.loadCurrentUser().pipe(catchError(() => of(null))));
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            MonacoEditorModule.forRoot({
                baseUrl: './assets/monaco/min/vs',
                defaultOptions: {
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    fontFamily: 'JetBrains Mono, Consolas, monospace',
                    lineNumbers: 'on',
                    renderLineHighlight: 'line',
                    scrollbar: { vertical: 'auto', horizontal: 'auto' }
                }
            })
        ),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withEnabledBlockingInitialNavigation()
        ),
        provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAuth,
            deps: [AuthService],
            multi: true
        },
        provideZonelessChangeDetection(),
        // The brand logo is intentionally served at a higher resolution than its smallest (44px header) render
        // so it stays crisp at the larger login size. Opt out of Angular's dev-only oversized-image warning
        // (NG0913) for plain <img> — it does not affect production.
        { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } },
        providePrimeNG({
            ripple: true,
            inputStyle: 'filled',
            theme: { preset: MyPreset, options: { darkModeSelector: '.app-dark' } }
        })
    ]
};
